import React, { useState, useEffect, useMemo } from 'react';
import { useShop } from '../context/ShopContext';
import { StatCounter } from '../components/StatCounter';
import { ProductCard } from '../components/ProductCard';
import {
  Heart,
  ShoppingBag,
  Gift,
  ArrowRight,
  ShieldCheck,
  Users,
  Recycle,
  School,
  Sparkles,
  ChevronRight,
  MapPin,
  Clock,
  Calendar,
  CheckCircle2,
  Newspaper,
  Eye
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { SchoolBadge } from '../components/SchoolBadge';
import { getSchoolColour } from '../utils/schoolUtils';
import { getTotalStock } from '../services/inventoryService';
import { perfDiagnostics } from '../services/perfDiagnostics';

export const HomePage: React.FC = () => {
  useEffect(() => {
    perfDiagnostics.mark('FIRST_CATALOGUE_RENDER');
    // Ensure paint has occurred then mark app interactive
    const rId = requestAnimationFrame(() => {
      perfDiagnostics.mark('APP_INTERACTIVE');
    });
    return () => cancelAnimationFrame(rId);
  }, []);

  const {
    products,
    schools,
    news,
    stats,
    settings,
    setActivePage,
    setSelectedSchoolFilter,
    setSelectedCategoryFilter,
    openProductDetail
  } = useShop();

  const [activeSchoolTab, setActiveSchoolTab] = useState('all');

  // Automatic Hero Image Rotation for In-Stock Products
  const inStockItems = useMemo(
    () => products.filter((p) => getTotalStock(p) > 0 && p.images && p.images.length > 0 && !p.hideFromWebsite),
    [products]
  );

  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);

  useEffect(() => {
    if (inStockItems.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentHeroIndex((prevIndex) => {
        let nextIndex = Math.floor(Math.random() * inStockItems.length);
        if (nextIndex === prevIndex) {
          nextIndex = (prevIndex + 1) % inStockItems.length;
        }
        return nextIndex;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [inStockItems.length]);

  const currentHeroProduct = inStockItems[currentHeroIndex] || null;
  const currentHeroImg = currentHeroProduct?.images[0] || 'https://images.unsplash.com/photo-1544816155-12df9643f363?auto=format&fit=crop&w=1000&q=80';
  const currentHeroSchool = schools.find((s) => s.id === currentHeroProduct?.schoolId);

  const activeSchools = schools.filter((s) => s.active !== false);

  const latestProducts = products.filter((p) => !p.hideFromWebsite).slice(0, 8);

  const filteredBySchool =
    activeSchoolTab === 'all'
      ? products.filter((p) => !p.hideFromWebsite).slice(0, 6)
      : products.filter((p) => p.schoolId === activeSchoolTab && !p.hideFromWebsite).slice(0, 6);

  const publishedNews = news.filter((n) => n.published).slice(0, 3);

  const features = [
    {
      title: 'Completely Free',
      desc: 'No payments, no fees, no subscriptions. Every uniform item is 100% free for Immingham families.',
      icon: <Gift className="w-6 h-6 text-emerald-500" />
    },
    {
      title: 'Helping Local Families',
      desc: 'Reducing back-to-school cost pressures so every child feels proud, smart and confident in class.',
      icon: <Heart className="w-6 h-6 text-rose-500" />
    },
    {
      title: 'Reducing Landfill Waste',
      desc: 'Giving outgrown, high-quality uniform items a second life instead of ending up in local landfill.',
      icon: <Recycle className="w-6 h-6 text-teal-500" />
    },
    {
      title: 'Supporting All Schools',
      desc: 'Providing uniform stock for primary academies and secondary schools across Immingham.',
      icon: <School className="w-6 h-6 text-blue-500" />
    },
    {
      title: 'Community Driven',
      desc: 'Powered by thoughtful donations from local parents who care about their neighbours.',
      icon: <Users className="w-6 h-6 text-purple-500" />
    },
    {
      title: 'Volunteer Led',
      desc: 'Run by friendly local volunteers at Bert Boyden Community Centre.',
      icon: <ShieldCheck className="w-6 h-6 text-amber-500" />
    }
  ];

  return (
    <div className="space-y-20 pb-16">
      
      {/* HERO BANNER SECTION */}
      <section className="relative bg-[#002366] text-white pt-12 pb-20 md:py-24 overflow-hidden rounded-b-[3.5rem] shadow-2xl border-b border-blue-900">
        
        {/* Decorative Background Accents */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-96 h-96 bg-sky-400/20 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Hero Content */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-sky-200 text-xs font-bold backdrop-blur-xl shadow-sm">
                <Heart className="w-4 h-4 fill-sky-300 text-sky-300" />
                <span>Giving School Uniform a Second Life</span>
              </div>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.1]">
                {settings.heroHeading || 'Free School Uniform for Immingham Families'}
              </h1>

              <p className="text-base sm:text-lg text-slate-200 max-w-2xl leading-relaxed mx-auto lg:mx-0 font-normal">
                {settings.heroSubheading || 'Helping local families save money by giving quality school uniforms a second life. Based at Bert Boyden Community Centre, Immingham.'}
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3.5 pt-2">
                <button
                  onClick={() => setActivePage('shop')}
                  className="w-full sm:w-auto px-8 py-4 rounded-full bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white font-bold text-sm shadow-xl shadow-blue-950/60 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2 border border-white/20"
                >
                  <ShoppingBag className="w-5 h-5" />
                  <span>Reserve Free Uniform</span>
                </button>

                <button
                  onClick={() => setActivePage('donate')}
                  className="w-full sm:w-auto px-8 py-4 rounded-full bg-white/15 hover:bg-white/25 text-white border border-white/30 font-bold text-sm backdrop-blur-xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2 shadow-lg"
                >
                  <Gift className="w-5 h-5 text-emerald-300" />
                  <span>Donate Uniform</span>
                </button>

                <button
                  onClick={() => setActivePage('how-it-works')}
                  className="w-full sm:w-auto px-6 py-4 text-slate-200 hover:text-white font-bold text-sm flex items-center justify-center gap-1 transition"
                >
                  <span>How It Works</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Trust Badges */}
              <div className="pt-4 flex flex-wrap items-center justify-center lg:justify-start gap-6 text-xs text-slate-300 font-medium">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-300" />
                  <span>No Customer Accounts</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-300" />
                  <span>100% Free Service</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-300" />
                  <span>Bert Boyden Hub Collection</span>
                </div>
              </div>

            </div>

            {/* Right Hero Image Poster Card */}
            <div className="lg:col-span-5">
              <div
                onClick={() => {
                  if (currentHeroProduct) {
                    openProductDetail(currentHeroProduct);
                  }
                }}
                className="relative mx-auto max-w-md lg:max-w-none rounded-3xl overflow-hidden border border-white/20 shadow-2xl bg-[#001c52]/80 backdrop-blur-xl group cursor-pointer"
                title={currentHeroProduct ? `Click to view ${currentHeroProduct.name}` : undefined}
              >
                <div className="relative w-full h-[420px] overflow-hidden bg-slate-900">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={currentHeroProduct ? currentHeroProduct.id : 'default-hero'}
                      src={currentHeroImg}
                      alt={currentHeroProduct ? currentHeroProduct.name : 'Uniform Loop Immingham'}
                      initial={{ opacity: 0, scale: 1.05 }}
                      animate={{ opacity: 0.9, scale: 1 }}
                      exit={{ opacity: 0, scale: 1.02 }}
                      transition={{ duration: 0.8 }}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </AnimatePresence>
                </div>
                
                {/* Overlay Poster Badge */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#001c52] via-transparent to-transparent p-6 flex flex-col justify-end pointer-events-none">
                  <div className="p-4 rounded-2xl bg-[#00143d]/90 backdrop-blur-xl border border-white/20 space-y-2 shadow-2xl pointer-events-auto">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-extrabold uppercase tracking-widest text-sky-300 flex items-center gap-1">
                        <Sparkles className="w-3 h-3 text-amber-300" />
                        In-Stock Item Showcase
                      </span>
                      <span className="text-[10px] bg-emerald-500/20 text-emerald-300 px-2.5 py-0.5 rounded-full font-bold border border-emerald-400/30">
                        In Stock Now
                      </span>
                    </div>

                    {currentHeroProduct ? (
                      <div className="space-y-1.5">
                        <div>
                          <SchoolBadge school={currentHeroSchool} schoolName={currentHeroSchool?.name || 'Immingham School'} size="xs" />
                        </div>
                        <h4 className="font-heading text-base font-extrabold text-white line-clamp-1">
                          {currentHeroProduct.name}
                        </h4>
                        <div className="flex items-center justify-between text-xs text-slate-200 pt-1">
                          <span>Size: <strong>{currentHeroProduct.size}</strong></span>
                          <span className="text-emerald-300 font-bold flex items-center gap-1 bg-emerald-950/60 px-2 py-0.5 rounded-md border border-emerald-500/30">
                            <Eye className="w-3 h-3" /> Click to view
                          </span>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="font-heading text-sm font-semibold italic text-slate-100">
                          "Giving uniform a second chance. Supporting our school. Supporting each other."
                        </p>
                        <p className="text-[11px] text-slate-300">
                          Drop off unwanted blazers, PE kits, jumpers &amp; trousers at school reception or Bert Boyden Community Centre.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ANIMATED COMMUNITY STATISTICS COUNTERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-20">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCounter
            value={stats.familiesHelped}
            label="Families Helped"
            suffix="+"
            icon={<Users className="w-6 h-6" />}
          />
          <StatCounter
            value={stats.itemsReused}
            label="Items Reused"
            suffix="+"
            icon={<ShoppingBag className="w-6 h-6" />}
          />
          <StatCounter
            value={stats.itemsDonated}
            label="Items Donated"
            suffix="+"
            icon={<Gift className="w-6 h-6" />}
          />
          <StatCounter
            value={stats.volunteers}
            label="Volunteers"
            icon={<Heart className="w-6 h-6" />}
          />
          <StatCounter
            value={stats.schoolsSupported}
            label="Local Schools"
            icon={<School className="w-6 h-6" />}
          />
        </div>
      </section>

      {/* FEATURE CARDS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12 space-y-2">
          <h2 className="font-heading text-3xl font-extrabold text-[#002366]">
            Why Uniform Loop?
          </h2>
          <p className="text-sm text-slate-600">
            Built purely for Immingham families with transparency, trust and ease.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feat, idx) => (
            <div
              key={idx}
              className="p-6 rounded-3xl bg-white border border-slate-200/80 shadow-sm hover:shadow-md transition-all space-y-3"
            >
              <div className="p-3 rounded-2xl bg-blue-50 w-fit border border-blue-100">{feat.icon}</div>
              <h3 className="font-heading font-bold text-[#002366] text-base">{feat.title}</h3>
              <p className="text-xs text-slate-600 leading-relaxed">{feat.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* LATEST UNIFORM ADDED SLIDER / GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">
              <Sparkles className="w-4 h-4" /> Live Inventory
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">
              Latest Uniform Added
            </h2>
          </div>
          <button
            onClick={() => setActivePage('shop')}
            className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 transition"
          >
            <span>View All Stock ({products.length})</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {latestProducts.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* SCHOOLS WE SUPPORT FILTERING */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#002366] rounded-3xl p-8 sm:p-12 text-white shadow-xl border border-blue-900 space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <h2 className="font-heading text-3xl font-extrabold text-white">
              Schools We Support In Immingham
            </h2>
            <p className="text-xs text-slate-200">
              Select a school below to see available free uniform stock.
            </p>
          </div>

          {/* School Filter Tabs */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => setActiveSchoolTab('all')}
              className={`px-4 py-2 rounded-full text-xs font-bold transition border flex items-center gap-2 ${
                activeSchoolTab === 'all'
                  ? 'bg-blue-600 text-white shadow-md border-blue-400'
                  : 'bg-white/10 text-slate-200 border-white/20 hover:bg-white/20 backdrop-blur-md'
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-sky-300 inline-block" />
              <span>All Schools</span>
            </button>

            {activeSchools.map((s) => {
              const sColour = getSchoolColour(s);
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveSchoolTab(s.id)}
                  className={`px-4 py-2 rounded-full text-xs font-bold transition border flex items-center gap-2 ${
                    activeSchoolTab === s.id
                      ? 'bg-blue-600 text-white shadow-md border-blue-400 ring-2 ring-white/30'
                      : 'bg-white/10 text-slate-200 border-white/20 hover:bg-white/20 backdrop-blur-md'
                  }`}
                >
                  <span
                    style={{ backgroundColor: sColour }}
                    className="w-2.5 h-2.5 rounded-full inline-block shrink-0 ring-1 ring-white/40 shadow-xs"
                  />
                  <span>{s.name}</span>
                </button>
              );
            })}
          </div>

          {/* School Products Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
            {filteredBySchool.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>

          <div className="text-center pt-4">
            <button
              onClick={() => {
                if (activeSchoolTab !== 'all') {
                  setSelectedSchoolFilter(activeSchoolTab);
                } else {
                  setSelectedSchoolFilter('');
                }
                setSelectedCategoryFilter('');
                setActivePage('shop');
              }}
              className="px-8 py-3.5 rounded-full bg-white text-[#002366] font-bold text-xs hover:bg-slate-100 transition inline-flex items-center gap-2 shadow-lg border border-white/80"
            >
              <span>Explore Full {activeSchoolTab === 'all' ? 'Immingham' : 'School'} Catalogue</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS (3 ANIMATED STEPS) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        <div className="text-center max-w-2xl mx-auto space-y-2">
          <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">
            Simple 3-Step Process
          </span>
          <h2 className="font-heading text-3xl font-extrabold text-[#002366]">
            How Uniform Loop Works
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
          
          <div className="p-8 rounded-3xl bg-white border border-slate-200/80 shadow-sm text-center space-y-4 relative hover:shadow-md transition-all">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-700 font-extrabold text-xl flex items-center justify-center mx-auto border border-blue-100">
              1
            </div>
            <h3 className="font-heading font-bold text-[#002366] text-lg">Browse Uniform</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Explore available blazers, jumpers, PE kits and trousers filtered by Immingham school, age and size.
            </p>
          </div>

          <div className="p-8 rounded-3xl bg-white border border-slate-200/80 shadow-sm text-center space-y-4 relative hover:shadow-md transition-all">
            <div className="w-14 h-14 rounded-2xl bg-sky-50 text-sky-700 font-extrabold text-xl flex items-center justify-center mx-auto border border-sky-100">
              2
            </div>
            <h3 className="font-heading font-bold text-[#002366] text-lg">Reserve Online</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Add needed items to your reservation basket. No payment or account registration required!
            </p>
          </div>

          <div className="p-8 rounded-3xl bg-white border border-slate-200/80 shadow-sm text-center space-y-4 relative hover:shadow-md transition-all">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-700 font-extrabold text-xl flex items-center justify-center mx-auto border border-emerald-100">
              3
            </div>
            <h3 className="font-heading font-bold text-[#002366] text-lg">Collect Locally</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Receive your reference code and pick up your reserved items at Bert Boyden Community Centre, Immingham.
            </p>
          </div>

        </div>
      </section>

      {/* COMMUNITY IMPACT & NEWS PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Environmental Impact Card */}
        <div className="lg:col-span-6 bg-gradient-to-br from-emerald-900 to-teal-950 text-white rounded-3xl p-8 shadow-xl space-y-6 flex flex-col justify-between">
          <div className="space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 text-emerald-300 flex items-center justify-center">
              <Recycle className="w-7 h-7" />
            </div>
            <h3 className="font-heading text-2xl font-bold">Community &amp; Environmental Impact</h3>
            <p className="text-xs text-emerald-100 leading-relaxed">
              Every year hundreds of perfectly good school uniforms are outgrown. Instead of ending up in landfill, these uniforms help another local Immingham family save money and reduce carbon emissions.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-emerald-800/80 text-xs">
            <div>
              <span className="text-emerald-300 block font-semibold">Estimated Landfill Reduction</span>
              <strong className="text-2xl font-bold text-white">~1.4 Tonnes</strong>
            </div>
            <div>
              <span className="text-emerald-300 block font-semibold">Average Family Saving</span>
              <strong className="text-2xl font-bold text-white">~£220 / Year</strong>
            </div>
          </div>
        </div>

        {/* Latest News & Announcements */}
        <div className="lg:col-span-6 bg-white rounded-3xl p-8 border border-slate-200/80 shadow-sm space-y-6">
          <div className="flex items-center justify-between border-b border-slate-200/60 pb-4">
            <div className="flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-blue-600" />
              <h3 className="font-heading font-bold text-[#002366] text-lg">News &amp; Announcements</h3>
            </div>
            <button
              onClick={() => setActivePage('news')}
              className="text-xs font-bold text-blue-600 hover:text-blue-800"
            >
              View All
            </button>
          </div>

          <div className="space-y-4">
            {publishedNews.map((n) => (
              <div key={n.id} className="flex gap-4 items-start group cursor-pointer" onClick={() => setActivePage('news')}>
                <img
                  src={n.image}
                  alt={n.title}
                  loading="lazy"
                  decoding="async"
                  className="w-16 h-16 rounded-2xl object-cover shrink-0 group-hover:scale-105 transition"
                />
                <div className="space-y-1">
                  <span className="text-[10px] font-bold text-blue-600 uppercase bg-blue-50 px-2.5 py-0.5 rounded-full">
                    {n.category}
                  </span>
                  <h4 className="font-heading font-bold text-xs text-slate-900 group-hover:text-blue-600 transition">
                    {n.title}
                  </h4>
                  <p className="text-[11px] text-slate-500 line-clamp-2">
                    {n.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </section>

    </div>
  );
};
